/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { View, Modal, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import ProductTileGroupView from '@airasia-phoenix/react-native/components/ProductTileGroupView';
import { getColor, Orientation } from '@airasia-phoenix/react-native/utils';
import { pd, useContainerWidth } from '@airasia-rn/ui';
import { ProductTilesData } from '../../models/homepageBlockTypes';
import HeaderView from '@airasia-phoenix/react-native/widgets/HeaderView';
import { FC, useMemo, useEffect, useRef, useState } from 'react';
import { ProductTileViewType } from '@airasia-phoenix/react-native/components';
import { calculateVisibleTiles } from '../../utils/productTilesHelper';
import ProductTileGroupWebView from '../../components/ProductTileGroupWebView';
import { useLogger } from '@airasia-rn/core-logger';
import {
  HomePageEvents,
  trackProductTileClick,
  trackProductTilesViewed,
} from '../../analytics/homepage';

type ProductTilesWebViewProps = {
  productTilesData: ProductTilesData;
  onProductTileClicked: (url: string, productId: string) => void;
  showAllTiles: boolean;
  setShowAllTiles: (show: boolean) => void;
};

// Stable reference so memo deps do not change on every render when `tiles` is absent.
const EMPTY_TILES: ProductTilesData['tiles'] = [];

const DEFAULT_SSR_CONTAINER_WIDTH = 1152;

const ProductTilesWebView: FC<ProductTilesWebViewProps> = ({
  productTilesData,
  onProductTileClicked,
  showAllTiles,
  setShowAllTiles,
}) => {
  const { containerWidth, onLayout } = useContainerWidth();
  const SHOW_MORE_TILES_COLUMNS = 5;
  const { logEvent } = useLogger();
  const hasTrackedView = useRef(false);
  const hasTrackedModalView = useRef(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  // Pre-measure (server HTML and first client render) the container reports
  // 0; assume the widest content column so the row is part of the server
  // HTML. The onLayout measurement corrects the count on narrower desktops.
  const effectiveContainerWidth = containerWidth || DEFAULT_SSR_CONTAINER_WIDTH;
  // Guard: a CMS block can ship without `tiles` (the prod schema allows it).
  const tiles = productTilesData?.tiles ?? EMPTY_TILES;
  const { visibleTileCount, hasMore } = useMemo(
    () => calculateVisibleTiles(effectiveContainerWidth, tiles.length),
    [effectiveContainerWidth, tiles.length],
  );

  const mappedTiles = useMemo(
    () =>
      tiles.map((tile, index) => ({
        id: tile.productId,
        type: ProductTileViewType.REGULAR,
        icon: { url: tile.iconUrl },
        primaryText: tile.title,
        secondaryText: tile.attractText,
        onPress: () => {
          logEvent(
            HomePageEvents.tap,
            trackProductTileClick({
              label: `${tile.productId}|${index + 1}`,
            }),
          );
          if (tile.redirectionUrl) {
            onProductTileClicked(tile.redirectionUrl, tile.productId);
          }
        },
      })),
    [logEvent, onProductTileClicked, tiles],
  );

  const visibleTiles = useMemo(
    () => mappedTiles.slice(0, visibleTileCount),
    [mappedTiles, visibleTileCount],
  );

  const showAllTile = useMemo(() => {
    if (!productTilesData.showAll || !hasMore) return null;

    return {
      id: 'showAll',
      type: ProductTileViewType.REGULAR,
      icon: {
        url: productTilesData.showAll.iconUrl,
        tint: getColor(pd.sys.colour.content_secondary),
      },
      primaryText: productTilesData.showAll.label,
      secondaryText: '',
      onPress: () => {
        logEvent(
          HomePageEvents.tap,
          trackProductTileClick({
            label: `showAll|${visibleTileCount + 1}`,
          }),
        );
        setShowAllTiles(true);
      },
    };
  }, [hasMore, logEvent, productTilesData, setShowAllTiles, visibleTileCount]);

  const homeTiles = useMemo(
    () => (showAllTile ? [...visibleTiles, showAllTile] : visibleTiles),
    [visibleTiles, showAllTile],
  );

  // Memoised: a fresh array every render would re-render every tile.
  const groupTiles = useMemo(
    () =>
      homeTiles.map((tile) => ({
        type: tile.type,
        iconUrl: tile.icon.url,
        tint: (tile.icon as { tint?: string }).tint,
        primaryText: tile.primaryText,
        secondaryText: tile.secondaryText,
        onPress: tile.onPress,
      })),
    [homeTiles],
  );

  useEffect(() => {
    if (!hasTrackedView.current && visibleTiles.length > 0) {
      hasTrackedView.current = true;
      logEvent(
        HomePageEvents.view,
        trackProductTilesViewed({
          label: visibleTiles.map((tile) => tile.id).join('|'),
        }),
      );
    }
  }, [logEvent, visibleTiles]);

  useEffect(() => {
    if (
      showAllTiles &&
      !hasTrackedModalView.current &&
      mappedTiles.length > 0
    ) {
      hasTrackedModalView.current = true;
      logEvent(
        HomePageEvents.view,
        trackProductTilesViewed({
          label: mappedTiles.map((tile) => tile.id).join('|'),
        }),
      );
    }
  }, [showAllTiles, logEvent, mappedTiles]);

  return (
    <View style={styles.modalContainer} onLayout={onLayout}>
      {/* Flexbox group with <img> icons so the row is complete in the server
          HTML; Phoenix ProductTileGroupView lays out from measurements. */}
      <ProductTileGroupWebView tiles={groupTiles} />
      {hydrated && (
        <Modal
          visible={showAllTiles}
          transparent
          animationType="none"
          onRequestClose={() => setShowAllTiles(false)}
        >
          <Pressable
            style={styles.modalOverlay}
            onPress={() => setShowAllTiles(false)}
          >
            <Pressable
              style={styles.modalContent}
              onPress={(e) => e.stopPropagation()}
            >
              <View style={styles.headerContainer}>
                <HeaderView
                  titleStart={productTilesData.showAll?.dialogText}
                  startIcon={{
                    icon: { icon: pd.icon.main.navigation.close },
                    onPress: () => setShowAllTiles(false),
                  }}
                />
              </View>

              <ProductTileGroupView
                productTileProp={mappedTiles}
                orientation={
                  new Orientation.VerticalGrid(
                    SHOW_MORE_TILES_COLUMNS,
                    0,
                    0,
                    pd.ref.space.spacing40.px,
                  )
                }
                customHorizonalSpacing={16}
              />
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create((pd) => ({
  modalOverlay: {
    flex: 1,
    backgroundColor: pd.sys.colour.content_primary.withAlpha(80).toString(),
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: 493,
    maxWidth: '90%',
    backgroundColor: pd.sys.colour.bg_primary.toString(),
    borderRadius: pd.ref.space.spacing40.px,
    paddingHorizontal: pd.ref.space.spacing50.px,
    paddingBottom: pd.ref.space.spacing50.px,
    overflow: 'hidden',
  },
  modalContainer: {
    paddingLeft: pd.ref.space.spacing40.px,
    backgroundColor: pd.sys.colour.container_none.toString(),
  },
  headerContainer: {
    marginHorizontal: -pd.ref.space.spacing50.px,
  },
}));

export default ProductTilesWebView;
